import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  components,
  type GroupBase,
  type MultiValueProps,
  type MultiValueRemoveProps,
  type OnChangeValue,
} from 'react-select';
import { type LoadOptions } from 'react-select-async-paginate';
import { type AsyncProps as ReactAsyncSelectProps } from 'react-select/async';

import { SelectMultiValueChip } from '../Select/SelectMultiValueChip';
import { getSelectClassNames, getSelectStyles } from '../Select/selectTheme';

import { getSelectComponents } from './selectComponents';
import { type SelectDataSource } from './SelectDataSource';
import { type AsyncOption } from './types';
import { useAsyncSelectHydration } from './useAsyncSelectHydration';

type Additional<TPageParam> = {
  pageParam?: TPageParam;
};

type ThemeProps<TItem> = Pick<
  ReactAsyncSelectProps<
    AsyncOption<TItem>,
    boolean,
    GroupBase<AsyncOption<TItem>>
  >,
  'classNames' | 'components' | 'styles'
>;

type UseAsyncSelectControlOptions<TItem, TPageParam, TFilters> =
  ThemeProps<TItem> & {
    readonly dataSource: SelectDataSource<TItem, TPageParam, TFilters>;
    readonly debounceTimeout: number;
    readonly emptyOption?: {
      readonly label: string;
      readonly value: string;
    };
    readonly errors: readonly string[];
    readonly filters?: TFilters;
    readonly isCreatable: boolean;
    readonly isMulti: boolean;
    readonly isRemovableValue?: (option: AsyncOption<TItem>) => boolean;
    readonly minimumSearchLength?: number;
    readonly onMultiValueClick?: (option: AsyncOption<TItem>) => void;
    readonly unresolvedLabel?: string;
    readonly value: string | null | readonly string[];
    readonly onChange: (
      value: string | null | string[],
      items: TItem | readonly TItem[] | undefined,
    ) => void;
  };

function getValues(
  value: string | null | readonly string[],
): readonly string[] {
  if (typeof value === 'string') {
    return value ? [value] : [];
  }

  return value?.filter(Boolean) ?? [];
}

// Reconciles controlled values with hydrated items and retained option state.
function getSelectedOptions<TItem>(
  items: readonly TItem[],
  selectedOptions: readonly AsyncOption<TItem>[],
  values: readonly string[],
  getOptionValue: (item: TItem) => string,
  toOption: (item: TItem) => AsyncOption<TItem>,
  getFallbackOption: (value: string) => AsyncOption<TItem> = (value) => ({
    label: value,
    status: 'unresolved',
    value,
  }),
): AsyncOption<TItem>[] {
  const itemsByValue = new Map(
    items.map((item) => [getOptionValue(item), item]),
  );
  const selectedOptionsByValue = new Map(
    selectedOptions.map((option) => [option.value, option]),
  );

  return values.map((selectedValue) => {
    const item = itemsByValue.get(selectedValue);
    return item
      ? toOption(item)
      : (selectedOptionsByValue.get(selectedValue) ??
          getFallbackOption(selectedValue));
  });
}

type UseAsyncSelectLoaderOptions<TItem, TPageParam, TFilters> = {
  readonly dataSource: SelectDataSource<TItem, TPageParam, TFilters>;
  readonly debounceTimeout: number;
  readonly emptyOption?: {
    readonly label: string;
    readonly value: string;
  };
  readonly filters?: TFilters;
  readonly minimumSearchLength?: number;
  readonly value: string | null | readonly string[];
};

function useAsyncSelectLoader<TItem, TPageParam, TFilters>({
  dataSource,
  debounceTimeout,
  emptyOption,
  filters,
  minimumSearchLength = 0,
  value,
}: UseAsyncSelectLoaderOptions<TItem, TPageParam, TFilters>) {
  const [selectedOptions, setSelectedOptions] = useState<AsyncOption<TItem>[]>(
    [],
  );
  const [hasLoadError, setHasLoadError] = useState(false);
  const valueRef = useRef(value);
  const latestRequestRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const pendingDebounceResolversRef = useRef<
    Array<(options: AsyncOption<TItem>[]) => void>
  >([]);
  const { getOptionLabel, getOptionValue } = dataSource;

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Converts a domain item into react-select's resolved option shape.
  const toOption = useCallback(
    (item: TItem): AsyncOption<TItem> => ({
      data: item,
      label: getOptionLabel(item),
      status: 'resolved',
      value: getOptionValue(item),
    }),
    [getOptionLabel, getOptionValue],
  );

  // Loads one page while allowing only the latest request to update control state.
  const loadPage = useCallback(
    async ({
      pageParam,
      search,
    }: {
      pageParam?: TPageParam;
      search: string;
    }) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      try {
        const page = await dataSource.loadOptions({
          filters,
          pageParam,
          search,
        });

        if (latestRequestRef.current === requestId) {
          setSelectedOptions((currentOptions) =>
            getSelectedOptions(
              page.items,
              currentOptions,
              getValues(valueRef.current),
              getOptionValue,
              toOption,
            ).filter(
              (option) =>
                option.status === 'resolved' || option.status === 'empty',
            ),
          );
          setHasLoadError(false);
        }

        return page;
      } catch {
        if (latestRequestRef.current === requestId) {
          setHasLoadError(true);
        }

        return undefined;
      }
    },
    [dataSource, filters, getOptionValue, toOption],
  );

  const withEmptyOption = useCallback(
    (options: AsyncOption<TItem>[]) =>
      emptyOption
        ? [{ ...emptyOption, status: 'empty' as const }, ...options]
        : options,
    [emptyOption],
  );

  const invalidateLoadState = useCallback(() => {
    latestRequestRef.current += 1;
    setHasLoadError(false);
  }, []);

  // Adapts a non-paginated data-source response to react-select options.
  const loadOptionsImmediately = useCallback(
    async (search: string) => {
      if (search.length < minimumSearchLength) {
        invalidateLoadState();
        return [];
      }

      const page = await loadPage({ search });
      return page ? withEmptyOption(page.items.map(toOption)) : [];
    },
    [
      invalidateLoadState,
      loadPage,
      minimumSearchLength,
      toOption,
      withEmptyOption,
    ],
  );

  // Coalesces rapid searches into one request while resolving every caller.
  const loadOptions = useCallback(
    (search: string) => {
      if (search.length < minimumSearchLength) {
        invalidateLoadState();
        clearTimeout(debounceTimerRef.current);
        const resolvers = pendingDebounceResolversRef.current;
        pendingDebounceResolversRef.current = [];
        for (const resolve of resolvers) {
          resolve([]);
        }
        return Promise.resolve([]);
      }

      if (debounceTimeout <= 0) {
        return loadOptionsImmediately(search);
      }

      return new Promise<AsyncOption<TItem>[]>((resolve) => {
        pendingDebounceResolversRef.current.push(resolve);
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          const resolvers = pendingDebounceResolversRef.current;
          pendingDebounceResolversRef.current = [];

          void loadOptionsImmediately(search).then((options) => {
            for (const resolvePending of resolvers) {
              resolvePending(options);
            }
          });
        }, debounceTimeout);
      });
    },
    [
      debounceTimeout,
      invalidateLoadState,
      loadOptionsImmediately,
      minimumSearchLength,
    ],
  );

  // Cancels pending debounce work without leaving react-select promises unsettled.
  useEffect(
    () => () => {
      clearTimeout(debounceTimerRef.current);
      for (const resolve of pendingDebounceResolversRef.current) {
        resolve([]);
      }
    },
    [],
  );

  // Adapts cursor or offset pagination to async-paginate's page contract.
  const loadPaginatedOptions = useCallback<
    LoadOptions<
      AsyncOption<TItem>,
      GroupBase<AsyncOption<TItem>>,
      Additional<TPageParam>
    >
  >(
    async (search, _loadedOptions, additional) => {
      if (search.length < minimumSearchLength) {
        invalidateLoadState();
        return {
          additional: { pageParam: dataSource.initialPageParam },
          hasMore: false,
          options: [],
        };
      }

      const page = await loadPage({
        pageParam: additional?.pageParam ?? dataSource.initialPageParam,
        search,
      });

      if (!page) {
        return {
          additional: { pageParam: undefined },
          hasMore: false,
          options: [],
        };
      }

      const isFirstPage =
        additional?.pageParam === undefined ||
        additional.pageParam === dataSource.initialPageParam;
      const options = page.items.map(toOption);

      return {
        additional: { pageParam: page.nextPageParam },
        hasMore: page.nextPageParam !== undefined,
        options: isFirstPage ? withEmptyOption(options) : options,
      };
    },
    [
      dataSource.initialPageParam,
      invalidateLoadState,
      loadPage,
      minimumSearchLength,
      toOption,
      withEmptyOption,
    ],
  );

  return {
    hasLoadError,
    loadOptions,
    loadPaginatedOptions,
    selectedOptions,
    setSelectedOptions,
    toOption,
  };
}

function getFallbackOption<TItem>(
  selectedValue: string,
  emptyOption: UseAsyncSelectControlOptions<TItem, never, never>['emptyOption'],
  failedValues: ReadonlySet<string>,
  resolvableValues: ReadonlySet<string>,
  isCreatable: boolean,
  unresolvedLabel?: string,
): AsyncOption<TItem> {
  if (selectedValue === emptyOption?.value) {
    return {
      label: emptyOption.label,
      status: 'empty',
      value: selectedValue,
    };
  }

  if (failedValues.has(selectedValue)) {
    return {
      label: unresolvedLabel ?? '',
      status: 'failed',
      value: selectedValue,
    };
  }

  const isResolvable = resolvableValues.has(selectedValue);
  return {
    label: isResolvable ? '' : selectedValue,
    status: isCreatable && !isResolvable ? 'freeText' : 'unresolved',
    value: selectedValue,
  };
}

function getNextOptions<TItem>(
  nextValue: OnChangeValue<AsyncOption<TItem>, boolean>,
  currentOptions: readonly AsyncOption<TItem>[],
  isMulti: boolean,
  isRemovableValue?: (option: AsyncOption<TItem>) => boolean,
) {
  const nextOptions = [nextValue]
    .flat()
    .filter((option): option is AsyncOption<TItem> => option !== null);
  const lockedOptions = isMulti
    ? currentOptions.filter(
        (option) =>
          isRemovableValue &&
          !isRemovableValue(option) &&
          !nextOptions.some(({ value }) => value === option.value),
      )
    : [];

  return [...lockedOptions, ...nextOptions].filter(
    (option, index, options) =>
      options.findIndex(({ value }) => value === option.value) === index,
  );
}

/**
 * Bridges our value-based select API with react-select's option-based API.
 *
 * Forms store only stable string values, while react-select needs complete
 * option objects to render labels. This hook loads and normalizes list items,
 * retains options chosen by the user, resolves externally assigned values
 * through optional TanStack queries, and adapts single/multi-select changes.
 */
export function useAsyncSelectControl<TItem, TPageParam, TFilters>({
  classNames,
  components: componentOverrides,
  dataSource,
  debounceTimeout,
  emptyOption,
  errors,
  filters,
  isCreatable,
  isMulti,
  isRemovableValue,
  minimumSearchLength,
  onChange,
  onMultiValueClick,
  styles,
  unresolvedLabel,
  value,
}: UseAsyncSelectControlOptions<TItem, TPageParam, TFilters>) {
  const {
    hasLoadError,
    loadOptions,
    loadPaginatedOptions,
    selectedOptions,
    setSelectedOptions,
    toOption,
  } = useAsyncSelectLoader({
    dataSource,
    debounceTimeout,
    emptyOption,
    filters,
    minimumSearchLength,
    value,
  });
  const { getOptionValue } = dataSource;

  const MultiValue = useMemo(
    () =>
      memo(
        ({
          data,
          removeProps,
        }: MultiValueProps<
          AsyncOption<TItem>,
          boolean,
          GroupBase<AsyncOption<TItem>>
        >) => (
          <SelectMultiValueChip
            data={data}
            isRemovable={!isRemovableValue || isRemovableValue(data)}
            onClick={onMultiValueClick}
            onDelete={removeProps.onClick}
          />
        ),
      ),
    [isRemovableValue, onMultiValueClick],
  );

  const MultiValueRemove = useMemo(
    () =>
      function MultiValueRemove(
        props: MultiValueRemoveProps<
          AsyncOption<TItem>,
          boolean,
          GroupBase<AsyncOption<TItem>>
        >,
      ) {
        const { data } = props;

        return !isRemovableValue || isRemovableValue(data) ? (
          <components.MultiValueRemove {...props} />
        ) : null;
      },
    [isRemovableValue],
  );

  // User selections already contain their full item. Only values assigned from
  // outside this control (initial data, resets, mirrored fields) need resolving.
  const values = getValues(value);
  const selectedValues = new Set(selectedOptions.map(({ value }) => value));
  const { failedValues, resolvableValues, resolvedItems } =
    useAsyncSelectHydration({
      dataSource,
      emptyValue: emptyOption?.value,
      getOptionValue,
      selectedValues,
      values,
    });
  // Assigns a renderable status to values that are empty, failed, or unresolved.
  const currentOptions = getSelectedOptions(
    resolvedItems,
    selectedOptions,
    values,
    getOptionValue,
    toOption,
    (selectedValue) =>
      getFallbackOption(
        selectedValue,
        emptyOption,
        failedValues,
        resolvableValues,
        isCreatable,
        unresolvedLabel,
      ),
  );
  // Restores locked values before translating option changes back to form values.
  const handleChange = useCallback(
    (nextValue: OnChangeValue<AsyncOption<TItem>, boolean>) => {
      const mergedOptions = getNextOptions(
        nextValue,
        currentOptions,
        isMulti,
        isRemovableValue,
      );
      const values = mergedOptions.map(({ value }) => value);
      const items = mergedOptions
        .filter(
          (
            option,
          ): option is Extract<AsyncOption<TItem>, { status: 'resolved' }> =>
            option.status === 'resolved',
        )
        .map(({ data }) => data);

      setSelectedOptions(mergedOptions);
      onChange(
        isMulti ? values : (values[0] ?? null),
        isMulti ? items : items[0],
      );
    },
    [currentOptions, isMulti, isRemovableValue, onChange],
  );

  return {
    additional: { pageParam: dataSource.initialPageParam },
    classNames: getSelectClassNames({
      classNames,
      hasErrors: errors.length > 0 || hasLoadError,
    }),
    components: getSelectComponents({
      ...componentOverrides,
      MultiValue: componentOverrides?.MultiValue ?? MultiValue,
      MultiValueRemove,
    }),
    handleChange,
    hasLoadError,
    isPaginated: dataSource.isPaginated,
    loadOptions,
    loadPaginatedOptions,
    styles: getSelectStyles(styles),
    value: isMulti ? currentOptions : (currentOptions[0] ?? null),
  };
}
